/**
 * "Book a Demo" enquiry dialog — posts to /api/public/demo-request.
 * Fires demo_form_started (first interaction) + demo_form_submitted (success).
 */
import { useRef, useState } from 'react';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { api, extractErrorMessage } from '@/lib/api';
import { getStoredUtm, marketingEvent } from '@/lib/marketing';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EMPTY = { name: '', email: '', phone: '', restaurant_name: '', message: '' };

export function DemoDialog({ open, onOpenChange }) {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [serverError, setServerError] = useState('');
  const startedRef = useRef(false);

  const set = (key, value) => {
    if (!startedRef.current) {
      startedRef.current = true;
      marketingEvent('demo_form_started');
    }
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Your name is required';
    if (!EMAIL_RE.test(form.email.trim())) e.email = 'Enter a valid email address';
    if (!form.restaurant_name.trim()) e.restaurant_name = 'Restaurant name is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    setServerError('');
    if (!validate()) return;
    setSubmitting(true);
    try {
      await api.post('/public/demo-request', {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        restaurant_name: form.restaurant_name.trim(),
        message: form.message.trim(),
        utm: getStoredUtm(),
      });
      marketingEvent('demo_form_submitted');
      setDone(true);
    } catch (err) {
      setServerError(extractErrorMessage(err, 'Could not send your request. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenChange = (next) => {
    onOpenChange(next);
    if (!next) {
      // Reset for the next visitor interaction, keep `started` per session.
      setTimeout(() => { setDone(false); setForm(EMPTY); setErrors({}); setServerError(''); }, 200);
    }
  };

  const fieldError = (key) =>
    errors[key] ? <p className="text-xs text-[color:var(--brand-danger)] mt-1" data-testid={`marketing-book-demo-${key}-error`}>{errors[key]}</p> : null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md bg-[color:var(--brand-surface)]" data-testid="marketing-book-demo-dialog">
        {done ? (
          <div className="py-6 text-center" data-testid="marketing-book-demo-success-message">
            <CheckCircle2 className="h-10 w-10 mx-auto text-[color:var(--brand-success)]" aria-hidden="true" />
            <h3 className="font-display text-xl font-semibold mt-3">Request received</h3>
            <p className="text-sm text-[color:var(--brand-text-muted)] mt-2">
              Thanks {form.name.split(' ')[0] || ''} — we&apos;ll be in touch shortly to arrange your demo.
            </p>
            <Button className="mt-5 h-11 rounded-xl bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)]" onClick={() => handleOpenChange(false)} data-testid="marketing-book-demo-close-button">
              Close
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display">Book a demo</DialogTitle>
              <DialogDescription>
                Tell us a little about your restaurant and we&apos;ll walk you through RotaHQ.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={onSubmit} className="space-y-3.5" noValidate>
              <div>
                <Label htmlFor="demo-name">Name</Label>
                <Input id="demo-name" value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Your name" aria-invalid={!!errors.name} className="mt-1" data-testid="marketing-book-demo-name-input" />
                {fieldError('name')}
              </div>
              <div>
                <Label htmlFor="demo-email">Email</Label>
                <Input id="demo-email" type="email" autoComplete="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="you@restaurant.com" aria-invalid={!!errors.email} className="mt-1" data-testid="marketing-book-demo-email-input" />
                {fieldError('email')}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                <div>
                  <Label htmlFor="demo-phone">Phone <span className="text-[color:var(--brand-text-muted)] font-normal">(optional)</span></Label>
                  <Input id="demo-phone" type="tel" value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="+44…" className="mt-1" data-testid="marketing-book-demo-phone-input" />
                </div>
                <div>
                  <Label htmlFor="demo-restaurant">Restaurant name</Label>
                  <Input id="demo-restaurant" value={form.restaurant_name} onChange={(e) => set('restaurant_name', e.target.value)} placeholder="Your restaurant" aria-invalid={!!errors.restaurant_name} className="mt-1" data-testid="marketing-book-demo-restaurant-input" />
                  {fieldError('restaurant_name')}
                </div>
              </div>
              <div>
                <Label htmlFor="demo-message">What do you want to improve? <span className="text-[color:var(--brand-text-muted)] font-normal">(optional)</span></Label>
                <Textarea id="demo-message" rows={3} value={form.message} onChange={(e) => set('message', e.target.value)} placeholder="e.g. We're still doing rotas in a spreadsheet…" className="mt-1" data-testid="marketing-book-demo-message-textarea" />
              </div>
              {serverError ? (
                <p className="text-sm text-[color:var(--brand-danger)]" data-testid="marketing-book-demo-error-message">{serverError}</p>
              ) : null}
              <Button type="submit" disabled={submitting} className="w-full h-11 rounded-xl bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] font-semibold transition-colors duration-150" data-testid="marketing-book-demo-submit-button">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Request a demo'}
              </Button>
              <p className="text-[11px] text-[color:var(--brand-text-muted)] text-center">We&apos;ll only use your details to arrange the demo.</p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
